import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Alert, Button, Checkbox, Popover } from "antd";
import { DeleteTwoTone, InfoCircleOutlined } from "@ant-design/icons";
import CustomModal from "../../components/CustomModal/CustomModal";
import InputField from "../../components/InputField/InputField";
import TextAreaField from "../../components/TextAreaField/TextAreaField";
import SelectInput from "../../components/SelectInput/SelectInput";
import DateField from "../../components/DateField/DateField";
import ImageUploadField from "../../components/ImageUploadField/ImageUploadField";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi } from "../../utils/apiService";
import { ADD_QUESTION, UPDATE_QUESTION } from "../../utils/apiPath";
import {
    DEFAULT_OPTION_SETS,
    QUESTION_TYPES,
    buildQuestionPayload,
    getAttemptCount,
    isChoiceType,
} from "./questionHelpers";

const DefaultOptionsPopoverContent = (
    <div style={{ minWidth: 200 }}>
        {DEFAULT_OPTION_SETS.map((set, si) => (
            <div key={si} style={{ marginBottom: si < DEFAULT_OPTION_SETS.length - 1 ? 12 : 0 }}>
                <strong style={{ display: "block", marginBottom: 4 }}>{set.label}</strong>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                    {set.options.map((opt, oi) => (
                        <li key={oi} style={{ fontSize: 12, lineHeight: "20px" }}>
                            {opt}
                        </li>
                    ))}
                </ol>
            </div>
        ))}
    </div>
);

const emptyForm = (moduleId) => ({
    moduleId: moduleId || null,
    questionText: "",
    questionMediaUrl: "",
    questionType: 1,
    answerKeyText: "",
    answerKeyDate: null,
    marks: 1,
    sortOrder: 1,
    isDefaultOptions: false,
    defaultOptionSet: 0,
    childQuestions: [],
    options: [
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
    ],
    syllabusId: null,
});

/**
 * Add / edit / duplicate a question.
 * `mode` is "add" | "edit"; `initialData` prefills it (a duplicate is an "add" with prefilled data).
 */
const QuestionFormModal = ({
    open,
    mode,
    initialData,
    defaultModuleId,
    moduleOptions,
    syllabusOptions,
    showModuleSelect,
    onClose,
    onSaved,
}) => {
    const [formData, setFormData] = useState(emptyForm(defaultModuleId));
    const [optionErrors, setOptionErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const attemptCount = mode === "edit" ? getAttemptCount(initialData) : 0;

    useEffect(() => {
        if (!open) return;
        setOptionErrors([]);
        if (initialData) {
            setFormData({
                ...emptyForm(defaultModuleId),
                ...initialData,
                moduleId: initialData.moduleId ?? defaultModuleId ?? null,
                syllabusId: initialData.syllabusId ?? initialData.SyllabusId ?? null,
                options:
                    initialData.options && initialData.options.length
                        ? initialData.options.map((o) => ({ ...o }))
                        : emptyForm().options,
                childQuestions: (initialData.childQuestions || []).map((cq) => ({
                    ...cq,
                    syllabusId: cq.syllabusId ?? cq.SyllabusId ?? null,
                })),
            });
        } else {
            setFormData(emptyForm(defaultModuleId));
        }
    }, [open, initialData, defaultModuleId]);

    const handleInputChange = (field, value) =>
        setFormData((prev) => ({ ...prev, [field]: value }));

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = { ...newOptions[index], [field]: value };
        if (field === "isCorrect" && value === true && formData.questionType === 3) {
            newOptions.forEach((opt, i) => {
                if (i !== index) opt.isCorrect = false;
            });
        }
        setFormData((prev) => ({ ...prev, options: newOptions }));
    };

    const addOption = () =>
        setFormData((prev) => ({
            ...prev,
            options: [...prev.options, { optionText: "", isCorrect: false }],
        }));

    const removeOption = (index) =>
        setFormData((prev) => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index),
        }));

    const handleAddChildQuestion = () =>
        setFormData((prev) => ({
            ...prev,
            childQuestions: [
                ...prev.childQuestions,
                {
                    questionId: null,
                    questionText: "",
                    questionMediaUrl: "",
                    questionType: 1,
                    answerKeyText: "",
                    marks: 1,
                    sortOrder: prev.childQuestions.length + 1,
                    isDefaultOptions: false,
                    options: [
                        { optionText: "", isCorrect: false },
                        { optionText: "", isCorrect: false },
                    ],
                    syllabusId: null,
                },
            ],
        }));

    const handleRemoveChildQuestion = (index) =>
        setFormData((prev) => ({
            ...prev,
            childQuestions: prev.childQuestions.filter((_, i) => i !== index),
        }));

    const handleChildQuestionChange = (index, field, value) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[index] = { ...newChildQuestions[index], [field]: value };
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleChildOptionChange = (childIndex, optionIndex, field, value) => {
        const newChildQuestions = [...formData.childQuestions];
        const childQuestion = newChildQuestions[childIndex];
        const newOptions = [...childQuestion.options];
        newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
        if (field === "isCorrect" && value === true && childQuestion.questionType === 3) {
            newOptions.forEach((opt, i) => {
                if (i !== optionIndex) opt.isCorrect = false;
            });
        }
        childQuestion.options = newOptions;
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleAddChildOption = (childIndex) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[childIndex].options.push({ optionText: "", isCorrect: false });
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleRemoveChildOption = (childIndex, optionIndex) => {
        const newChildQuestions = [...formData.childQuestions];
        newChildQuestions[childIndex].options = newChildQuestions[childIndex].options.filter(
            (_, i) => i !== optionIndex
        );
        setFormData((prev) => ({ ...prev, childQuestions: newChildQuestions }));
    };

    const handleSubmit = async () => {
        if (!formData.questionText.trim()) {
            errorToast("Question Text is required");
            return;
        }
        if (!formData.moduleId) {
            errorToast("Please select a module");
            return;
        }
        if (isChoiceType(formData.questionType) && !formData.isDefaultOptions) {
            const newOptionErrors = formData.options.map((o) =>
                o.optionText.trim() ? "" : "Option is required"
            );
            if (newOptionErrors.some((e) => e)) {
                setOptionErrors(newOptionErrors);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = buildQuestionPayload(formData, {
                moduleId: formData.moduleId,
                keepIds: mode === "edit",
            });

            const result =
                mode === "add"
                    ? await postApi(ADD_QUESTION, payload)
                    : await postApi(UPDATE_QUESTION, {
                        ...payload,
                        questionId: initialData?.questionId ?? initialData?.id,
                    });

            if (result.statusCode === 200) {
                successToast(result.message);
                onSaved?.();
            } else {
                errorToast(result.message);
            }
        } catch {
            errorToast("Operation failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <CustomModal
            open={open}
            title={mode === "edit" ? "Edit Question" : "Add Question"}
            onClose={onClose}
            primaryText={mode === "edit" ? "Update" : "Add"}
            onPrimary={handleSubmit}
            primaryProps={{ loading: isSubmitting }}
            width={800}
        >
            {attemptCount > 0 && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`${attemptCount} ${attemptCount === 1 ? "student has" : "students have"} already answered this question`}
                    description="Their results keep the version they answered, so past reports do not change. Your edit applies to students who take it from now on."
                />
            )}

            <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <SelectInput
                        title="Question Type"
                        options={QUESTION_TYPES}
                        value={formData.questionType}
                        onChange={(val) => handleInputChange("questionType", val)}
                        required
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div className="question-marks-row">
                        <InputField
                            title="Sort Order"
                            type="number"
                            value={formData.sortOrder}
                            onChange={(e) => handleInputChange("sortOrder", e.target.value)}
                            required
                        />
                    </div>
                </div>
            </div>

            {showModuleSelect && (
                <div style={{ marginBottom: 16 }}>
                    <SelectInput
                        title="Module"
                        options={moduleOptions}
                        value={formData.moduleId}
                        onChange={(val) => handleInputChange("moduleId", val)}
                        placeholder="Select module"
                        showSearch
                        required
                    />
                </div>
            )}

            <div style={{ marginBottom: 16 }}>
                <SelectInput
                    title="Syllabus"
                    options={syllabusOptions}
                    value={formData.syllabusId}
                    onChange={(val) => handleInputChange("syllabusId", val)}
                    placeholder="Select syllabus (Optional)"
                    required
                />
            </div>

            <TextAreaField
                title="Question Text"
                value={formData.questionText}
                onChange={(e) => handleInputChange("questionText", e.target.value)}
                required
                rows={3}
            />

            <div style={{ marginTop: 16 }}>
                <span className="input-label" style={{ display: "block", marginBottom: 8 }}>
                    Question Image (Optional)
                </span>
                <ImageUploadField
                    value={formData.questionMediaUrl}
                    onChange={(_, url) => handleInputChange("questionMediaUrl", url)}
                />
            </div>

            {formData.questionType === 1 && (
                <InputField
                    title="Answer Key / Model Answer"
                    value={formData.answerKeyText}
                    onChange={(e) => handleInputChange("answerKeyText", e.target.value)}
                    placeholder="Enter correct answer"
                    required
                />
            )}

            {formData.questionType === 2 && (
                <TextAreaField
                    title="Answer Key / Model Answer"
                    value={formData.answerKeyText}
                    onChange={(e) => handleInputChange("answerKeyText", e.target.value)}
                    placeholder="Enter correctness criteria"
                    rows={3}
                />
            )}

            {formData.questionType === 5 && (
                <DateField
                    title="Answer Key Date"
                    value={formData.answerKeyDate ? dayjs(formData.answerKeyDate) : null}
                    onChange={(date) => handleInputChange("answerKeyDate", date)}
                    placeholder="Select correct date"
                />
            )}

            {isChoiceType(formData.questionType) && (
                <div className="options-container">
                    <div
                        className="options-header"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 10,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span>
                                Options ({formData.questionType === 3 ? "Single Choice" : "Multi Choice"})
                            </span>
                            <Checkbox
                                checked={formData.defaultOptionSet === 1}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    handleInputChange("isDefaultOptions", checked);
                                    handleInputChange("defaultOptionSet", checked ? 1 : 0);
                                }}
                            >
                                Default Options 1
                            </Checkbox>
                            <Checkbox
                                checked={formData.defaultOptionSet === 2}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    handleInputChange("isDefaultOptions", checked);
                                    handleInputChange("defaultOptionSet", checked ? 2 : 0);
                                }}
                            >
                                Default Options 2
                            </Checkbox>
                            <Popover
                                trigger="hover"
                                placement="topLeft"
                                title={<span style={{ fontWeight: 600 }}>Default Option Sets</span>}
                                content={DefaultOptionsPopoverContent}
                            >
                                <InfoCircleOutlined
                                    style={{ color: "#faad14", cursor: "pointer", fontSize: 15 }}
                                />
                            </Popover>
                        </div>
                        {!formData.isDefaultOptions && (
                            <Button type="dashed" size="small" onClick={addOption}>
                                + Add Option
                            </Button>
                        )}
                    </div>

                    {!formData.isDefaultOptions &&
                        formData.options.map((opt, idx) => (
                            <div key={idx} className="option-row">
                                <div className="option-check-wrapper">
                                    <input
                                        type={formData.questionType === 3 ? "radio" : "checkbox"}
                                        className="option-check"
                                        checked={opt.isCorrect}
                                        onChange={(e) =>
                                            handleOptionChange(idx, "isCorrect", e.target.checked)
                                        }
                                        name="modalOptionRadio"
                                        title="Mark as correct answer"
                                    />
                                </div>
                                <div className="option-input-wrapper">
                                    <InputField
                                        placeholder={`Option ${idx + 1}`}
                                        value={opt.optionText}
                                        onChange={(e) => {
                                            handleOptionChange(idx, "optionText", e.target.value);
                                            if (optionErrors[idx])
                                                setOptionErrors((prev) => {
                                                    const updated = [...prev];
                                                    updated[idx] = "";
                                                    return updated;
                                                });
                                        }}
                                        errorText={optionErrors[idx] || ""}
                                    />
                                </div>
                                <div className="option-action-wrapper">
                                    {formData.options.length > 2 && (
                                        <Button
                                            danger
                                            type="text"
                                            icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                            onClick={() => removeOption(idx)}
                                            className="option-delete-btn"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            )}

            {formData.questionType === 6 && (
                <div
                    className="child-questions-section"
                    style={{ marginTop: 24, borderTop: "1px solid #eee", paddingTop: 16 }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 16,
                        }}
                    >
                        <h4 style={{ margin: 0 }}>Child Questions</h4>
                        <Button type="primary" size="small" onClick={handleAddChildQuestion}>
                            + Add Sub-Question
                        </Button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                        {formData.childQuestions.map((child, cIdx) => (
                            <div
                                key={cIdx}
                                style={{
                                    background: "#fafafa",
                                    padding: 16,
                                    borderRadius: 8,
                                    border: "1px solid #f0f0f0",
                                    position: "relative",
                                }}
                            >
                                <div style={{ position: "absolute", right: 8, top: 8 }}>
                                    <Button
                                        danger
                                        type="text"
                                        icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                        onClick={() => handleRemoveChildQuestion(cIdx)}
                                    />
                                </div>
                                <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                                    <div style={{ flex: 1 }}>
                                        <SelectInput
                                            title={`Sub-Question ${cIdx + 1} Type`}
                                            options={QUESTION_TYPES.filter((t) => t.value !== 6)}
                                            value={child.questionType}
                                            onChange={(val) =>
                                                handleChildQuestionChange(cIdx, "questionType", val)
                                            }
                                            required
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <InputField
                                            title="Sort Order"
                                            type="number"
                                            value={child.sortOrder}
                                            onChange={(e) =>
                                                handleChildQuestionChange(
                                                    cIdx,
                                                    "sortOrder",
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <SelectInput
                                        title="Syllabus"
                                        options={syllabusOptions}
                                        value={child.syllabusId}
                                        onChange={(val) =>
                                            handleChildQuestionChange(cIdx, "syllabusId", val)
                                        }
                                        placeholder="Select syllabus (Optional)"
                                    />
                                </div>

                                <TextAreaField
                                    title="Question Text"
                                    value={child.questionText}
                                    onChange={(e) =>
                                        handleChildQuestionChange(cIdx, "questionText", e.target.value)
                                    }
                                    required
                                    rows={2}
                                />

                                {isChoiceType(child.questionType) && (
                                    <div className="options-container" style={{ marginTop: 12 }}>
                                        <div
                                            className="options-header"
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: 10,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 10,
                                                }}
                                            >
                                                <span>
                                                    Options (
                                                    {child.questionType === 3
                                                        ? "Single Choice"
                                                        : "Multi Choice"}
                                                    )
                                                </span>
                                                <Checkbox
                                                    checked={(child.defaultOptionSet || 0) === 1}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        handleChildQuestionChange(
                                                            cIdx,
                                                            "isDefaultOptions",
                                                            checked
                                                        );
                                                        handleChildQuestionChange(
                                                            cIdx,
                                                            "defaultOptionSet",
                                                            checked ? 1 : 0
                                                        );
                                                    }}
                                                >
                                                    Use Default Options 1
                                                </Checkbox>
                                                <Checkbox
                                                    checked={(child.defaultOptionSet || 0) === 2}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        handleChildQuestionChange(
                                                            cIdx,
                                                            "isDefaultOptions",
                                                            checked
                                                        );
                                                        handleChildQuestionChange(
                                                            cIdx,
                                                            "defaultOptionSet",
                                                            checked ? 2 : 0
                                                        );
                                                    }}
                                                >
                                                    Use Default Options 2
                                                </Checkbox>
                                                <Popover
                                                    trigger="hover"
                                                    placement="topLeft"
                                                    title={
                                                        <span style={{ fontWeight: 600 }}>
                                                            Default Option Sets
                                                        </span>
                                                    }
                                                    content={DefaultOptionsPopoverContent}
                                                >
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: "#faad14",
                                                            cursor: "pointer",
                                                            fontSize: 15,
                                                        }}
                                                    />
                                                </Popover>
                                            </div>
                                            {!child.isDefaultOptions && (
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    onClick={() => handleAddChildOption(cIdx)}
                                                >
                                                    + Add Option
                                                </Button>
                                            )}
                                        </div>

                                        {!child.isDefaultOptions &&
                                            child.options.map((opt, oIdx) => (
                                                <div key={oIdx} className="option-row">
                                                    <div className="option-check-wrapper">
                                                        <input
                                                            type={
                                                                child.questionType === 3
                                                                    ? "radio"
                                                                    : "checkbox"
                                                            }
                                                            className="option-check"
                                                            checked={opt.isCorrect}
                                                            onChange={(e) =>
                                                                handleChildOptionChange(
                                                                    cIdx,
                                                                    oIdx,
                                                                    "isCorrect",
                                                                    e.target.checked
                                                                )
                                                            }
                                                            name={`childOptionRadio-${cIdx}`}
                                                            title="Mark as correct answer"
                                                        />
                                                    </div>
                                                    <div className="option-input-wrapper">
                                                        <InputField
                                                            placeholder={`Option ${oIdx + 1}`}
                                                            value={opt.optionText}
                                                            onChange={(e) =>
                                                                handleChildOptionChange(
                                                                    cIdx,
                                                                    oIdx,
                                                                    "optionText",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="option-action-wrapper">
                                                        {child.options.length > 2 && (
                                                            <Button
                                                                danger
                                                                type="text"
                                                                icon={
                                                                    <DeleteTwoTone
                                                                        twoToneColor={[
                                                                            "#ff4d4f",
                                                                            "#fff1f0",
                                                                        ]}
                                                                    />
                                                                }
                                                                onClick={() =>
                                                                    handleRemoveChildOption(cIdx, oIdx)
                                                                }
                                                                className="option-delete-btn"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {child.questionType === 1 && (
                                    <InputField
                                        title="Answer Key"
                                        value={child.answerKeyText}
                                        onChange={(e) =>
                                            handleChildQuestionChange(
                                                cIdx,
                                                "answerKeyText",
                                                e.target.value
                                            )
                                        }
                                    />
                                )}
                                {child.questionType === 2 && (
                                    <TextAreaField
                                        title="Answer Key"
                                        value={child.answerKeyText}
                                        onChange={(e) =>
                                            handleChildQuestionChange(
                                                cIdx,
                                                "answerKeyText",
                                                e.target.value
                                            )
                                        }
                                    />
                                )}
                                {child.questionType === 5 && (
                                    <DateField
                                        title="Answer Key Date"
                                        value={child.answerKeyDate ? dayjs(child.answerKeyDate) : null}
                                        onChange={(date) =>
                                            handleChildQuestionChange(cIdx, "answerKeyDate", date)
                                        }
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </CustomModal>
    );
};

QuestionFormModal.propTypes = {
    open: PropTypes.bool,
    mode: PropTypes.oneOf(["add", "edit"]),
    initialData: PropTypes.object,
    defaultModuleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    moduleOptions: PropTypes.array,
    syllabusOptions: PropTypes.array,
    showModuleSelect: PropTypes.bool,
    onClose: PropTypes.func,
    onSaved: PropTypes.func,
};

export default QuestionFormModal;
